/**
 * <s-product-image-zoom> — PDP image zoom preloader and lightbox stepper.
 *
 * Gallery buttons open native dialogs declaratively via commandfor/show-modal.
 * This component warms the selected zoom image on hover/focus, and lets the
 * open dialog step through every product image (prev/next buttons, arrow keys,
 * swipe) by swapping the image inside the already-open dialog, so no dialog is
 * closed and reopened mid-transition. Closing restores the dialog's own image.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 *   dialog [data-zoom-step]         — optional prev (-1) / next (1) buttons.
 *   dialog [data-zoom-counter]      — optional live counter; data-template holds "%c".
 */
const SWAP_ATTRS = ['src', 'srcset', 'sizes', 'alt', 'width', 'height'];

class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];
  #swipeStartX = null;
  #justSwiped = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('click', this.#handleStepClick, opts);
    this.addEventListener('pointerdown', this.#handlePointerDown, opts);
    this.addEventListener('pointerup', this.#handlePointerUp, opts);
    // Document-level so arrow keys work wherever focus sits inside the modal.
    document.addEventListener('keydown', this.#handleKeydown, opts);

    for (const dialog of this.#dialogs()) {
      dialog.addEventListener('close', () => this.#reset(dialog), opts);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #dialogs() {
    return [...this.querySelectorAll('dialog')].filter((dialog) => dialog.querySelector('img[data-zoom-image]'));
  }

  #openDialog() {
    return this.#dialogs().find((dialog) => dialog.open) ?? null;
  }

  #imageOf(dialog) {
    return dialog.querySelector('img[data-zoom-image]');
  }

  #step(delta) {
    const dialog = this.#openDialog();
    const dialogs = this.#dialogs();
    if (!dialog || dialogs.length < 2) return;

    const ownIndex = dialogs.indexOf(dialog);
    const shownIndex = dialog.dataset.zoomShown ? Number(dialog.dataset.zoomShown) : ownIndex;
    const targetIndex = (shownIndex + delta + dialogs.length) % dialogs.length;

    const image = this.#imageOf(dialog);
    const source = this.#imageOf(dialogs[targetIndex]);
    if (!image || !source) return;

    // Remember this dialog's own image once so close can restore it.
    if (!image.dataset.zoomOriginal) {
      const original = {};
      for (const attr of SWAP_ATTRS) original[attr] = image.getAttribute(attr);
      image.dataset.zoomOriginal = JSON.stringify(original);
    }

    const values = targetIndex === ownIndex && image.dataset.zoomOriginal
      ? JSON.parse(image.dataset.zoomOriginal)
      : Object.fromEntries(SWAP_ATTRS.map((attr) => [attr, source.getAttribute(attr)]));
    this.#apply(image, values);

    dialog.dataset.zoomShown = String(targetIndex);
    this.#updateCounter(dialog, targetIndex + 1);
  }

  #apply(image, values) {
    // Clear srcset first so the browser never pairs the new src with the old candidates.
    image.removeAttribute('srcset');
    image.loading = 'eager';
    for (const attr of SWAP_ATTRS) {
      const value = values[attr];
      if (value === null || value === undefined) image.removeAttribute(attr);
      else image.setAttribute(attr, value);
    }
  }

  #updateCounter(dialog, position) {
    const counter = dialog.querySelector('[data-zoom-counter]');
    const template = counter?.getAttribute('data-template');
    if (counter && template) counter.textContent = template.replace('%c', String(position));
  }

  #reset(dialog) {
    const image = this.#imageOf(dialog);
    if (image?.dataset.zoomOriginal) {
      this.#apply(image, JSON.parse(image.dataset.zoomOriginal));
    }
    const ownIndex = this.#dialogs().indexOf(dialog);
    delete dialog.dataset.zoomShown;
    this.#updateCounter(dialog, ownIndex + 1);
  }

  #handleStepClick = (event) => {
    if (!(event.target instanceof Element)) return;

    // Clicking the start or end half of the enlarged photo steps back or forward.
    const photo = event.target.closest('img[data-zoom-image]');
    if (this.#justSwiped) { this.#justSwiped = false; return; }
    if (photo && this.contains(photo) && this.#openDialog()) {
      const rect = photo.getBoundingClientRect();
      const rtl = getComputedStyle(this).direction === 'rtl';
      const inStartHalf = (event.clientX - rect.left < rect.width / 2) !== rtl;
      this.#step(inStartHalf ? -1 : 1);
      return;
    }

    const button = event.target.closest('[data-zoom-step]');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    event.stopPropagation();
    this.#step(Number(button.getAttribute('data-zoom-step')) || 1);
  };

  #handleKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!this.#openDialog()) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const forward = (event.key === 'ArrowRight') !== rtl;
    event.preventDefault();
    this.#step(forward ? 1 : -1);
  };

  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' || !this.#openDialog()) return;
    this.#swipeStartX = event.clientX;
  };

  #handlePointerUp = (event) => {
    if (this.#swipeStartX === null) return;
    const distance = event.clientX - this.#swipeStartX;
    this.#swipeStartX = null;
    if (Math.abs(distance) < 50) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    this.#justSwiped = true;
    setTimeout(() => { this.#justSwiped = false; }, 400);
    this.#step((distance < 0) !== rtl ? 1 : -1);
  };

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
